import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { ROUTE_TYPE_META } from "@/lib/routeTypes";
import { TrendingUp, Sun, Calendar, ArrowUpRight } from "lucide-react";

const SEGMENT_KEYS = ["small", "big", "pickup", "walmart", "split"];

function StatCard({ label, value, sub, icon: Icon, testId, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="text-left bg-white border border-[#E2E2E8] p-5 rounded-none md:rounded-lg hover:border-[#2563EB] hover:shadow-[0_2px_8px_-2px_rgba(37,99,235,0.15)] transition-colors duration-150 group cursor-pointer"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-slate-400 group-hover:text-[#2563EB]" />}
      </div>
      <div className="mt-3 font-heading text-4xl font-semibold text-slate-900 flex items-center gap-2">
        {value}
        <ArrowUpRight className="w-5 h-5 text-slate-300 group-hover:text-[#2563EB] transition-colors duration-150" />
      </div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </button>
  );
}

function TypeCard({ typeKey, count, onClick }) {
  const meta = ROUTE_TYPE_META[typeKey];
  return (
    <button
      onClick={onClick}
      data-testid={`type-card-${typeKey}`}
      className="text-left p-5 rounded-none md:rounded-lg border hover:shadow-[0_4px_12px_-4px_rgba(15,23,42,0.15)] transition-colors duration-150 cursor-pointer"
      style={{ backgroundColor: meta.bg, borderColor: meta.border }}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: meta.text }}>
          {meta.label} Routes
        </div>
        <ArrowUpRight className="w-4 h-4 opacity-40" style={{ color: meta.text }} />
      </div>
      <div className="mt-3 font-heading text-4xl font-semibold" style={{ color: meta.text }}>{count}</div>
      <div className="mt-1 text-xs font-mono-data opacity-70" style={{ color: meta.text }}>{meta.description}</div>
    </button>
  );
}

function PeriodToggle({ value, onChange, testId }) {
  return (
    <div className="inline-flex items-center bg-slate-100 rounded-md p-0.5" data-testid={testId}>
      {["week", "month"].map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          data-testid={`${testId}-${p}`}
          className={`px-3 py-1 text-xs font-medium rounded transition-colors duration-150 ${
            value === p ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
          }`}
        >
          {p === "week" ? "Week" : "Month"}
        </button>
      ))}
    </div>
  );
}

function TimeseriesChart({ period }) {
  const { data } = useQuery({
    queryKey: ["timeseries", period],
    queryFn: async () => (await api.get(`/timeseries?period=${period}`)).data,
  });
  if (!data) return <div className="h-[260px] flex items-center justify-center text-sm text-slate-500">Loading…</div>;

  const chartData = data.series.map((d) => {
    const date = new Date(`${d.day}T00:00:00Z`);
    const label = period === "week"
      ? date.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" })
      : date.toLocaleDateString("en-US", { month: "numeric", day: "numeric", timeZone: "UTC" });
    return { label, count: d.count, day: d.day };
  });

  return (
    <div style={{ width: "100%", height: 260 }} data-testid={`timeseries-chart-${period}`}>
      <ResponsiveContainer>
        <BarChart data={chartData} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} interval={period === "month" ? 2 : 0} />
          <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
          <Tooltip cursor={{ fill: "#F8FAFC" }} contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
          <Bar dataKey="count" radius={[4, 4, 0, 0]} fill="#2563EB">
            {chartData.map((_, i) => <Cell key={`cell-${chartData[i].label}`} fill="#2563EB" />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function TopDriversList({ period }) {
  const nav = useNavigate();
  const { data } = useQuery({
    queryKey: ["top-drivers", period],
    queryFn: async () => (await api.get(`/top-drivers?period=${period}`)).data,
  });
  if (!data) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  if (!data.top_drivers.length) return <div className="p-6 text-sm text-slate-500">No entries in this {period} yet.</div>;
  return (
    <ul data-testid={`top-drivers-list-${period}`}>
      {data.top_drivers.map((d) => (
        <li key={d.driver_name}>
          <button
            onClick={() => nav(`/drivers?driver=${encodeURIComponent(d.driver_name)}`)}
            data-testid={`top-driver-${d.driver_name}`}
            className="w-full flex items-center justify-between px-5 py-3 border-b border-[#E2E2E8] last:border-b-0 hover:bg-slate-50 transition-colors duration-150 text-left"
          >
            <span className="text-sm font-medium">{d.driver_name}</span>
            <span className="flex items-center gap-3">
              <span className="text-sm font-mono-data text-slate-700">{d.count} routes</span>
              <ArrowUpRight className="w-4 h-4 text-slate-400" />
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [timePeriod, setTimePeriod] = useState("week");
  const [topPeriod, setTopPeriod] = useState("week");

  const { data: week } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
  });
  const { data: today } = useQuery({
    queryKey: ["today"],
    queryFn: async () => (await api.get("/today")).data,
    refetchInterval: 60_000,
  });

  if (!week || !today) return <div className="p-8 text-slate-500">Loading…</div>;

  const weekLabel = new Date(week.week_start).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const sinceLabel = new Date(today.since).toLocaleString("en-US", { hour: "numeric", minute: "2-digit", month: "short", day: "numeric" });

  const chartData = SEGMENT_KEYS.map((k) => ({
    name: ROUTE_TYPE_META[k].label,
    key: k,
    count: today.type_counts[k] || 0,
    color: ROUTE_TYPE_META[k].text,
  }));

  return (
    <div className="p-6 md:p-8 max-w-7xl">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Overview</div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight mt-1">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Week starting {weekLabel} · Today rolls on a 14-hour window · split routes count once</p>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard testId="stat-week-total" label="Routes This Week" value={week.total_this_week} icon={TrendingUp} onClick={() => nav("/routes")} />
        <StatCard testId="stat-today-total" label="Today's Routes (14h)" value={today.total} sub={`Since ${sinceLabel}`} icon={Sun} onClick={() => nav("/routes")} />
        <StatCard testId="stat-paste" label="New Paste" value="+" sub="Add today's roster" icon={Calendar} onClick={() => nav("/paste")} />
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {SEGMENT_KEYS.map((k) => (
            <TypeCard key={k} typeKey={k} count={today.type_counts[k] || 0} onClick={() => nav(`/routes?tab=${k}`)} />
          ))}
        </div>

        <div className="bg-white border border-[#E2E2E8] rounded-none md:rounded-lg p-5" data-testid="today-chart">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg font-medium">Today's Segments</h3>
            <span className="text-xs text-slate-500 font-mono-data">14h window</span>
          </div>
          <div className="mt-4" style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 24, left: 4, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "#0F172A", fontWeight: 500 }} width={70} />
                <Tooltip cursor={{ fill: "#F8FAFC" }} contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry) => <Cell key={entry.key} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 pt-3 border-t border-[#E2E2E8] flex items-center justify-between">
            <span className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Total Today</span>
            <span data-testid="today-total-inline" className="font-heading text-2xl font-semibold text-slate-900">{today.total}</span>
          </div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E2E2E8] rounded-none md:rounded-lg">
          <div className="px-5 py-4 border-b border-[#E2E2E8] flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-heading text-lg font-medium">Routes Over Time</h2>
            <PeriodToggle value={timePeriod} onChange={setTimePeriod} testId="timeseries-toggle" />
          </div>
          <div className="p-5">
            <TimeseriesChart period={timePeriod} />
          </div>
        </div>

        <div className="bg-white border border-[#E2E2E8] rounded-none md:rounded-lg">
          <div className="px-5 py-4 border-b border-[#E2E2E8] flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-heading text-lg font-medium">Top Drivers</h2>
            <PeriodToggle value={topPeriod} onChange={setTopPeriod} testId="top-drivers-toggle" />
          </div>
          <TopDriversList period={topPeriod} />
        </div>
      </div>
    </div>
  );
}
